<!-- src/views/TillsView.vue -->

<template>
  <div class="section coredeck-page-section">
    <!-- =====================================================
         HEADER
         ===================================================== -->
    <div class="row valign-wrapper">
      <div class="col s12 m6">
        <h4>Tills</h4>
      </div>

      <div class="col s12 m6 right-align">
        <button
          class="btn waves-effect waves-light"
          @click="addTill"
          :disabled="isSubmitting || venueModeLoading"
        >
          Add Till
        </button>

        <button
          class="btn waves-effect waves-light"
          style="margin-left: 8px"
          @click="submitTills"
          :disabled="!canSubmit || isSubmitting || venueModeLoading"
          :title="
            !canSubmit ? 'Missing venue / week ending / trading date' : 'Submit tills'
          "
        >
          <span v-if="isSubmitting"> Submitting… </span>

          <span v-else> Submit Tills </span>
        </button>
      </div>
    </div>

    <!-- =====================================================
         VENUE MODE LOADING
         ===================================================== -->
    <div
      v-if="venueModeLoading"
      class="card-panel blue-grey lighten-5 blue-grey-text text-darken-4"
    >
      Loading venue till settings...
    </div>

    <!-- =====================================================
         VENUE MODE ERROR
         ===================================================== -->
    <div
      v-else-if="venueModeError"
      class="card-panel amber lighten-4 brown-text text-darken-4"
    >
      {{ venueModeError }}

      <div style="margin-top: 6px">Tills are using the standard cash venue mode.</div>
    </div>

    <!-- =====================================================
         CASHLESS INFORMATION
         ===================================================== -->
    <div v-else-if="isCashless" class="card-panel teal lighten-5 teal-text text-darken-4">
      <div class="cashless-heading">
        <i class="material-icons"> credit_card </i>

        <strong> Cashless venue </strong>
      </div>

      <div style="margin-top: 8px">
        Cash denomination and float inputs are not required for this venue. Till counted
        totals are calculated from PDQ only.
      </div>
    </div>

    <!-- =====================================================
         SUMMARY CARD
         ===================================================== -->
    <div v-if="!venueModeLoading" class="row">
      <div class="col s12">
        <div class="card">
          <div class="card-content">
            <span class="card-title"> Till Summary </span>

            <!--
              Summary is allowed to scroll on genuinely narrow screens.
            -->
            <div class="summary-table-wrap">
              <table class="striped">
                <thead>
                  <tr>
                    <th>Till</th>

                    <th v-if="!isCashless" class="right-align">Cash Total</th>

                    <th class="right-align">PDQ Total</th>

                    <th class="right-align">X-Read</th>

                    <th class="right-align">Variance</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="t in tills" :key="'summary-' + t.id">
                    <td>
                      {{ t.id }}
                    </td>

                    <td v-if="!isCashless" class="right-align">
                      {{ gbp(computeCashTotal(t)) }}
                    </td>

                    <td class="right-align">
                      {{ gbp(computePdqTotal(t)) }}
                    </td>

                    <td class="right-align">
                      {{ gbp(computeXReadTotal(t)) }}
                    </td>

                    <td class="right-align">
                      <strong
                        :class="{
                          'red-text': computeVariance(t) < 0,
                        }"
                      >
                        {{ gbp(computeVariance(t)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr v-if="!tills.length">
                    <td :colspan="isCashless ? 4 : 5" class="grey-text">
                      No tills created
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         INFO
         ===================================================== -->
    <div v-if="infoMsg" class="row">
      <div class="col s12">
        <div class="card blue lighten-5">
          <div class="card-content blue-text text-darken-3">
            {{ infoMsg }}
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         ERROR
         ===================================================== -->
    <div v-if="errorMsg" class="row">
      <div class="col s12">
        <div class="card red lighten-5">
          <div class="card-content red-text text-darken-2">
            <strong> Error: </strong>

            {{ errorMsg }}
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         TILL CARDS
         ===================================================== -->
    <div v-if="!venueModeLoading" class="row">
      <div v-for="till in tills" :key="till.id" class="col s12 m6 l4">
        <div class="card till-card">
          <div class="card-content">
            <!-- =================================================
                 CARD TITLE
                 ================================================= -->
            <div class="till-card-heading">
              <span class="card-title"> Till {{ till.id }} </span>

              <span v-if="isCashless" class="cashless-till-label"> Cashless </span>
            </div>

            <!-- =================================================
                 IMPORTANT:
                 DO NOT ADD responsive-table HERE.

                 Materialize's responsive-table CSS was causing
                 the broken layout shown in your screenshot.
                 ================================================= -->
            <div class="till-table-wrap">
              <table class="striped till-entry-table">
                <colgroup>
                  <col class="till-label-column" />
                  <col class="till-value-column" />
                </colgroup>

                <thead>
                  <tr>
                    <th>
                      {{ isCashless ? "Payments" : "Cash" }}
                    </th>

                    <th class="right-align">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  <!-- =================================================
                       CASH INPUTS
                       CASH VENUES ONLY
                       ================================================= -->
                  <template v-if="!isCashless">
                    <tr v-for="row in cashOnlyRows" :key="row.key">
                      <td>
                        {{ row.label }}
                      </td>

                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          class="till-money-input"
                          :value="displayMoneyValue(till[row.key])"
                          @input="onMoneyInput(till, row.key, $event.target.value)"
                          :disabled="isSubmitting"
                        />
                      </td>
                    </tr>

                    <tr class="total-row">
                      <td>
                        <strong> Cash Total </strong>
                      </td>

                      <td class="right-align">
                        <strong>
                          {{ gbp(computeCashTotal(till)) }}
                        </strong>
                      </td>
                    </tr>

                    <tr class="section-break">
                      <td colspan="2"></td>
                    </tr>

                    <tr>
                      <td>Float</td>

                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          class="till-money-input"
                          :value="displayMoneyValue(till.lessFloat)"
                          @input="onMoneyInput(till, 'lessFloat', $event.target.value)"
                          :disabled="isSubmitting"
                        />
                      </td>
                    </tr>

                    <tr class="section-break">
                      <td colspan="2"></td>
                    </tr>
                  </template>

                  <!-- =================================================
                       PDQ
                       ================================================= -->
                  <tr class="section-header-row">
                    <td colspan="2">
                      <strong> PDQ </strong>
                    </td>
                  </tr>

                  <tr>
                    <td>Input</td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till.pdqInput)"
                        @input="onMoneyInput(till, 'pdqInput', $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong> Total PDQ </strong>
                    </td>

                    <td class="right-align">
                      <strong>
                        {{ gbp(computePdqTotal(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong> SubTotal </strong>
                    </td>

                    <td class="right-align">
                      <strong>
                        {{ gbp(computeSubTotal(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr class="section-break">
                    <td colspan="2"></td>
                  </tr>

                  <!-- =================================================
                       X-READ
                       ================================================= -->
                  <tr class="section-header-row">
                    <td colspan="2">
                      <strong> X-Read </strong>
                    </td>
                  </tr>

                  <tr>
                    <td>Liquor</td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till.xReadLiquor)"
                        @input="onMoneyInput(till, 'xReadLiquor', $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>Food</td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till.xReadFood)"
                        @input="onMoneyInput(till, 'xReadFood', $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>Door</td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till.xReadDoor)"
                        @input="onMoneyInput(till, 'xReadDoor', $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>Tickets</td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till.xReadTickets)"
                        @input="onMoneyInput(till, 'xReadTickets', $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>Other</td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till.xReadOther)"
                        @input="onMoneyInput(till, 'xReadOther', $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong> Totals </strong>
                    </td>

                    <td class="right-align">
                      <strong>
                        {{ gbp(computeXReadTotal(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong> Variance </strong>
                    </td>

                    <td class="right-align">
                      <strong
                        :class="{
                          'red-text': computeVariance(till) < 0,
                        }"
                      >
                        {{ gbp(computeVariance(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <!-- =================================================
                       COMMENTS
                       ================================================= -->
                  <tr class="comments-row">
                    <td colspan="2">
                      <label class="comments-label" :for="'till-notes-' + till.id">
                        Issues / Comments
                      </label>

                      <textarea
                        :id="'till-notes-' + till.id"
                        v-model="till.notes"
                        class="materialize-textarea till-comments"
                        placeholder="Add any issues, corrections, or comments for this till..."
                        :disabled="isSubmitting"
                      ></textarea>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";

import { useRoute } from "vue-router";

import { doc, getDoc } from "firebase/firestore";

import { auth, authReady, db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { upsertTillsForTradingDay } from "@/scripts/sqlApiHelper/tillsApi";

const route = useRoute();

/* =========================================================
   VENUE CONTEXT
   ========================================================= */

const context = useVenueContext();

function unrefish(x) {
  return x && typeof x === "object" && "value" in x ? x.value : x;
}

/* =========================================================
   DATE HELPERS
   ========================================================= */

function toLocalISODate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    return "";
  }

  const y = d.getFullYear();

  const m = String(d.getMonth() + 1).padStart(2, "0");

  const day = String(d.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
}

function isoFromUnknown(raw) {
  if (!raw) {
    return "";
  }

  if (typeof raw === "string") {
    const s = raw.trim();

    if (!s) {
      return "";
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return s;
    }

    const d = new Date(s);

    if (!Number.isNaN(d.getTime())) {
      return toLocalISODate(d);
    }

    return s.replace(/\//g, "-");
  }

  if (typeof raw === "object" && typeof raw.toDate === "function") {
    return toLocalISODate(raw.toDate());
  }

  if (raw instanceof Date) {
    return toLocalISODate(raw);
  }

  return "";
}

function getLocalStorageValue(keys) {
  if (typeof window === "undefined") {
    return "";
  }

  for (const key of keys) {
    const value = localStorage.getItem(key);

    if (value) {
      return value;
    }
  }

  return "";
}

function getRouteValue(keys) {
  for (const key of keys) {
    const value = route.params?.[key] || route.query?.[key];

    if (value) {
      return value;
    }
  }

  return "";
}

function getSundayWeekEndingFromISO(dateISO) {
  if (!dateISO) {
    return "";
  }

  const d = new Date(`${dateISO}T00:00:00`);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const day = d.getDay();

  const daysUntilSunday = day === 0 ? 0 : 7 - day;

  d.setDate(d.getDate() + daysUntilSunday);

  return toLocalISODate(d);
}

/* =========================================================
   RESOLVED CONTEXT VALUES
   ========================================================= */

const venueId = computed(() => {
  return (
    unrefish(context.venueId) ||
    unrefish(context.venue_id) ||
    unrefish(context.activeVenueId) ||
    getLocalStorageValue([
      "venueId",
      "venue_id",
      "activeVenueId",
      "selectedVenueId",
      "currentVenueId",
      "venue",
    ]) ||
    getRouteValue(["venueId", "venue_id", "venue"]) ||
    ""
  );
});

const tradingDateISO = computed(() => {
  const raw =
    unrefish(context.tradingDate) ||
    unrefish(context.trading_date) ||
    unrefish(context.businessDate) ||
    unrefish(context.business_date) ||
    unrefish(context.selectedDate) ||
    getLocalStorageValue([
      "tradingDate",
      "tradingDateISO",
      "trading_date",
      "businessDate",
      "business_date",
      "selectedDate",
      "currentTradingDate",
    ]) ||
    getRouteValue(["tradingDate", "trading_date", "businessDate", "date"]);

  const resolved = isoFromUnknown(raw);

  return resolved || toLocalISODate(new Date());
});

const weekEndingISO = computed(() => {
  const raw =
    unrefish(context.weekEndingDate) ||
    unrefish(context.weekEnding) ||
    unrefish(context.weekEndingISO) ||
    unrefish(context.week_ending_date) ||
    getLocalStorageValue([
      "weekEndingDate",
      "weekEnding",
      "weekEndingISO",
      "week_ending_date",
      "currentWeekEnding",
    ]) ||
    getRouteValue([
      "weekEnding",
      "weekEndingISO",
      "weekEndingDate",
      "week_ending_date",
      "weekId",
    ]);

  const resolved = isoFromUnknown(raw);

  return resolved || getSundayWeekEndingFromISO(tradingDateISO.value);
});

/* =========================================================
   STATE
   ========================================================= */

const tills = ref([]);

const isSubmitting = ref(false);

const errorMsg = ref("");

const infoMsg = ref("");

/* =========================================================
   CASH / CASHLESS MODE

   Mirrors Petty Cash:

   venues/{venueId}.is_cashless === true
   ========================================================= */

const venueModeLoading = ref(false);

const venueModeError = ref("");

const isCashless = ref(false);

async function loadVenueMode() {
  venueModeError.value = "";

  isCashless.value = false;

  const id = String(venueId.value || "").trim();

  if (!id) {
    return;
  }

  venueModeLoading.value = true;

  try {
    const venueSnap = await getDoc(doc(db, "venues", id));

    if (!venueSnap.exists()) {
      throw new Error(`Venue "${id}" could not be found.`);
    }

    const data = venueSnap.data() || {};

    isCashless.value = data.is_cashless === true;

    if (isCashless.value) {
      clearAllCashFields();
    }
  } catch (e) {
    console.error("[TillsView] Failed to load venue cashless setting:", e);

    /*
     * Same fail-safe behaviour as Petty Cash.
     */
    isCashless.value = false;

    venueModeError.value = e?.message || "Could not read the venue cashless setting.";
  } finally {
    venueModeLoading.value = false;
  }
}

/* =========================================================
   SUBMIT AVAILABILITY
   ========================================================= */

const canSubmit = computed(() => {
  return Boolean(
    venueId.value &&
      weekEndingISO.value &&
      tradingDateISO.value &&
      tills.value.length &&
      !venueModeLoading.value
  );
});

/* =========================================================
   CASH ROW DEFINITIONS
   ========================================================= */

const cashOnlyRows = [
  {
    key: "note50",
    label: "£50",
  },
  {
    key: "note20",
    label: "£20",
  },
  {
    key: "note10",
    label: "£10",
  },
  {
    key: "note5",
    label: "£5",
  },
  {
    key: "coin2",
    label: "£2",
  },
  {
    key: "coin1",
    label: "£1",
  },
  {
    key: "p50",
    label: "50p",
  },
  {
    key: "p20",
    label: "20p",
  },
  {
    key: "p10",
    label: "10p",
  },
  {
    key: "p5",
    label: "5p",
  },
  {
    key: "copper",
    label: "Copper",
  },
];

/* =========================================================
   NUMBER HELPERS
   ========================================================= */

function toNumber(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function clamp2(v) {
  return Math.round(toNumber(v) * 100) / 100;
}

function displayMoneyValue(v) {
  const n = toNumber(v);

  return n === 0 ? "" : n;
}

function onMoneyInput(till, key, raw) {
  if (raw === "" || raw == null) {
    till[key] = 0;

    return;
  }

  const n = Number(raw);

  if (Number.isNaN(n)) {
    return;
  }

  till[key] = clamp2(Math.max(0, n));
}

function gbp(n) {
  return clamp2(n).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* =========================================================
   CASH HELPERS
   ========================================================= */

function clearCashFields(till) {
  if (!till) {
    return;
  }

  cashOnlyRows.forEach((row) => {
    till[row.key] = 0;
  });

  till.lessFloat = 0;
}

function clearAllCashFields() {
  tills.value.forEach((till) => {
    clearCashFields(till);
  });
}

/* =========================================================
   CALCULATIONS
   ========================================================= */

function computeCashTotal(till) {
  if (isCashless.value) {
    return 0;
  }

  return cashOnlyRows.reduce((total, row) => total + toNumber(till[row.key]), 0);
}

function computePdqTotal(till) {
  return toNumber(till.pdqInput);
}

function computeSubTotal(till) {
  /*
   * CASHLESS:
   * PDQ only.
   *
   * CASH:
   * Cash + PDQ - Float.
   */
  if (isCashless.value) {
    return computePdqTotal(till);
  }

  return computeCashTotal(till) + computePdqTotal(till) - toNumber(till.lessFloat);
}

function computeXReadTotal(till) {
  return (
    toNumber(till.xReadLiquor) +
    toNumber(till.xReadFood) +
    toNumber(till.xReadDoor) +
    toNumber(till.xReadTickets) +
    toNumber(till.xReadOther)
  );
}

function computeVariance(till) {
  return computeSubTotal(till) - computeXReadTotal(till);
}

/* =========================================================
   TILL STATE
   ========================================================= */

function buildEmptyTill(id) {
  const till = {
    id,
  };

  cashOnlyRows.forEach((row) => {
    till[row.key] = 0;
  });

  till.lessFloat = 0;

  till.pdqInput = 0;

  till.xReadLiquor = 0;

  till.xReadFood = 0;

  till.xReadDoor = 0;

  till.xReadTickets = 0;

  till.xReadOther = 0;

  till.notes = "";

  return till;
}

function addTill() {
  const nextId = tills.value.length
    ? Math.max(...tills.value.map((x) => Number(x.id) || 0)) + 1
    : 1;

  tills.value.push(buildEmptyTill(nextId));
}

function ensureTwoTills() {
  if (tills.value.length > 2) {
    tills.value.splice(2);
  }

  while (tills.value.length < 2) {
    addTill();
  }
}

function resetAllTills() {
  tills.value.forEach((till) => {
    cashOnlyRows.forEach((row) => {
      till[row.key] = 0;
    });

    till.lessFloat = 0;

    till.pdqInput = 0;

    till.xReadLiquor = 0;

    till.xReadFood = 0;

    till.xReadDoor = 0;

    till.xReadTickets = 0;

    till.xReadOther = 0;

    till.notes = "";
  });
}

/* =========================================================
   FIRESTORE LINE BUILDING
   ========================================================= */

function buildLinesForTill(till) {
  const lines = [];

  /*
   * Cash data is only stored for a cash venue.
   */
  if (!isCashless.value) {
    for (const row of cashOnlyRows) {
      lines.push({
        line_type: "cash",

        label: row.label,

        amount: clamp2(till[row.key]),
      });
    }

    lines.push({
      line_type: "cash_total",

      label: "Cash Total",

      amount: clamp2(computeCashTotal(till)),
    });

    lines.push({
      line_type: "float",

      label: "Float",

      amount: clamp2(till.lessFloat),
    });
  }

  /*
   * PDQ
   */
  lines.push({
    line_type: "pdq",

    label: "Input",

    amount: clamp2(till.pdqInput),
  });

  lines.push({
    line_type: "pdq_total",

    label: "Total PDQ",

    amount: clamp2(computePdqTotal(till)),
  });

  /*
   * Subtotal
   */
  lines.push({
    line_type: "subtotal",

    label: "SubTotal",

    amount: clamp2(computeSubTotal(till)),
  });

  /*
   * X-Read
   */
  lines.push({
    line_type: "xread",

    label: "Liquor",

    amount: clamp2(till.xReadLiquor),
  });

  lines.push({
    line_type: "xread",

    label: "Food",

    amount: clamp2(till.xReadFood),
  });

  lines.push({
    line_type: "xread",

    label: "Door",

    amount: clamp2(till.xReadDoor),
  });

  lines.push({
    line_type: "xread",

    label: "Tickets",

    amount: clamp2(till.xReadTickets),
  });

  lines.push({
    line_type: "xread",

    label: "Other",

    amount: clamp2(till.xReadOther),
  });

  lines.push({
    line_type: "declared",

    label: "X-Read Total",

    amount: clamp2(computeXReadTotal(till)),
  });

  lines.push({
    line_type: "variance",

    label: "Variance",

    amount: clamp2(computeVariance(till)),
  });

  return lines;
}

/* =========================================================
   SAVE CONTEXT
   ========================================================= */

watch(
  [venueId, weekEndingISO, tradingDateISO],
  ([v, we, td]) => {
    if (typeof window === "undefined") {
      return;
    }

    if (v) {
      localStorage.setItem("venueId", v);
    }

    if (we) {
      localStorage.setItem("weekEnding", we);
    }

    if (td) {
      localStorage.setItem("tradingDate", td);
    }
  },
  {
    immediate: true,
  }
);

/* =========================================================
   WATCH VENUE
   ========================================================= */

watch(
  venueId,
  async (newVenueId, oldVenueId) => {
    if (!newVenueId) {
      isCashless.value = false;

      venueModeError.value = "";

      return;
    }

    if (newVenueId !== oldVenueId || oldVenueId === undefined) {
      await loadVenueMode();
    }
  },
  {
    immediate: true,
  }
);

/* =========================================================
   INITIALISE
   ========================================================= */

onMounted(() => {
  ensureTwoTills();
});

/* =========================================================
   SUBMIT
   ========================================================= */

async function submitTills() {
  try {
    errorMsg.value = "";

    infoMsg.value = "";

    await authReady;

    if (!auth.currentUser) {
      throw new Error("Not logged in");
    }

    if (venueModeLoading.value) {
      throw new Error("Venue settings are still loading.");
    }

    if (!canSubmit.value) {
      throw new Error(
        `Missing required data. Venue: ${venueId.value || "missing"}, week ending: ${
          weekEndingISO.value || "missing"
        }, trading date: ${tradingDateISO.value || "missing"}.`
      );
    }

    isSubmitting.value = true;

    /*
     * Never allow stale cash fields to be submitted
     * from a cashless venue.
     */
    if (isCashless.value) {
      clearAllCashFields();
    }

    const payload = {
      venue_id: venueId.value,

      week_ending_date: weekEndingISO.value,

      trading_date: tradingDateISO.value,

      tills: tills.value.map((till, index) => {
        const declared_total = clamp2(computeXReadTotal(till));

        const counted_total = clamp2(computeSubTotal(till));

        const notes = String(till.notes || "").trim();

        return {
          till_number: Number(till.id) || index + 1,

          declared_total,

          counted_total,

          variance: clamp2(counted_total - declared_total),

          notes: notes || null,

          lines: buildLinesForTill(till),
        };
      }),
    };

    await upsertTillsForTradingDay(payload);

    resetAllTills();

    infoMsg.value = "Tills submitted successfully.";
  } catch (e) {
    console.error("[TillsView] Submit failed:", e);

    errorMsg.value = e?.message || String(e);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
/* =========================================================
   TILL CARDS
   ========================================================= */

.till-card {
  width: 100%;
  border-radius: 12px;
}

.till-card .card-content {
  width: 100%;
  box-sizing: border-box;
}

/* =========================================================
   CARD HEADING
   ========================================================= */

.till-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.till-card-heading .card-title {
  margin: 0;
}

/* =========================================================
   CASHLESS LABEL
   ========================================================= */

.cashless-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cashless-heading .material-icons {
  font-size: 22px;
}

.cashless-till-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  text-transform: uppercase;
  background: #e0f2f1;
  color: #00695c;
}

/* =========================================================
   TILL TABLE

   IMPORTANT:
   This is intentionally NOT a Materialize responsive-table.
   ========================================================= */

.till-table-wrap {
  width: 100%;
  overflow: visible;
}

.till-entry-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.till-label-column {
  width: 52%;
}

.till-value-column {
  width: 48%;
}

.till-entry-table th,
.till-entry-table td {
  box-sizing: border-box;
  vertical-align: middle;
}

.till-entry-table th:first-child,
.till-entry-table td:first-child {
  padding-left: 8px;
  padding-right: 10px;
}

.till-entry-table th:last-child,
.till-entry-table td:last-child {
  padding-left: 10px;
  padding-right: 8px;
}

/* =========================================================
   INPUTS
   ========================================================= */

.till-money-input {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  text-align: right;
}

/*
 * Materialize input defaults need overriding inside this
 * fixed-width table.
 */
.till-entry-table input[type="number"] {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

/* =========================================================
   SECTION HEADER ROWS
   ========================================================= */

.section-header-row td {
  font-weight: 700;
}

.total-row td {
  font-weight: 700;
}

.section-break td {
  height: 10px;
  padding: 0 !important;
  background: transparent !important;
}

/* =========================================================
   COMMENTS
   ========================================================= */

.comments-row td {
  padding-top: 20px !important;
}

.comments-label {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  font-weight: 700;
  color: #424242;
}

.till-comments {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  min-height: 85px;
  padding: 10px !important;
  box-sizing: border-box !important;
  border: 1px solid #ddd !important;
  border-radius: 8px;
  background: #fff;
  resize: vertical;
}

.till-comments:focus {
  border: 1px solid #aaa !important;
  box-shadow: none !important;
}

/* =========================================================
   SUMMARY TABLE
   ========================================================= */

.summary-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* =========================================================
   MOBILE
   ========================================================= */

@media only screen and (max-width: 600px) {
  .row.valign-wrapper {
    display: block;
  }

  .card .card-content {
    padding: 14px !important;
  }

  .btn,
  .btn-small,
  button.btn,
  button.btn-small {
    width: 100%;
    margin: 4px 0;
  }

  .till-card-heading {
    flex-wrap: wrap;
    gap: 8px;
  }

  /*
   * Still keep each entry as TWO columns on mobile.
   */
  .till-label-column {
    width: 48%;
  }

  .till-value-column {
    width: 52%;
  }

  .till-entry-table th,
  .till-entry-table td {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  .till-money-input,
  .till-entry-table input[type="number"] {
    width: 100% !important;
    min-width: 0 !important;
  }

  .till-comments {
    width: 100% !important;
    min-width: 0 !important;
  }
}
</style>
